import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, BedDouble, Plane, Car, ArrowRight, AlertCircle, Shield, Users, Ticket, Wifi, ShieldCheck, Package, MapPin, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';

const SERVICES = [
  {
    category: 'Flights',
    items: [
      {
        name: 'Aviasales',
        desc: 'Compare flight prices from hundreds of airlines and travel agencies. Find the cheapest flights instantly.',
        url: 'https://aviasales.tpm.lv/3ogiO1hK',
        icon: Plane,
        color: 'from-orange-500 to-orange-600',
        tagline: 'Compare & Save',
      },
      {
        name: 'Expedia Flights',
        desc: 'Search and compare flights from hundreds of airlines. Bundle with hotels for extra savings and earn rewards.',
        url: 'https://expedia.com/affiliates/expedia-home.Aim0oVX',
        icon: Plane,
        color: 'from-emerald-600 to-emerald-700',
        tagline: 'Bundle & Save',
      },
      {
        name: 'TraveLean',
        desc: 'Search and compare flights, hotels, and vacation packages from top providers.',
        url: 'https://travelean.com',
        icon: Plane,
        color: 'from-sky-500 to-sky-600',
        tagline: 'Discounted Tickets Guaranteed',
      },
    ],
  },
  {
    category: 'Hotels & Accommodation',
    items: [
      {
        name: 'Booking.com',
        desc: 'Search millions of hotels, apartments, and resorts worldwide with free cancellation on most properties.',
        url: 'https://www.booking.com',
        icon: BedDouble,
        color: 'from-ocean to-sky',
        tagline: 'Best Price Guarantee',
      },
      {
        name: 'Expedia',
        desc: 'Bundle flights + hotels for extra savings. Access exclusive member-only deals and rewards.',
        url: 'https://expedia.com/affiliates/expedia-home.Aim0oVX',
        icon: BedDouble,
        color: 'from-emerald-600 to-emerald-700',
        tagline: 'Bundle & Save',
      },
      {
        name: 'TraveLean',
        desc: 'Search and compare hotels and vacation packages from top providers at competitive prices.',
        url: 'https://travelean.com',
        icon: BedDouble,
        color: 'from-sky-500 to-sky-600',
        tagline: 'Great Hotel Deals',
      },
    ],
  },
  {
    category: 'Car Rentals',
    items: [
      {
        name: 'Discovercars',
        desc: 'Compare car rental deals from top providers worldwide. Best price guaranteed with free cancellation.',
        url: 'https://discovercars.tpm.lv/bpojtima',
        icon: Car,
        color: 'from-blue-500 to-blue-600',
        tagline: 'Best Price Guaranteed',
      },
      {
        name: 'Qeeq',
        desc: 'Smart car rental comparison platform. Book with confidence using verified reviews and transparent pricing.',
        url: 'https://qeeq.tpm.lv/buBkpzmg',
        icon: Car,
        color: 'from-violet-500 to-violet-600',
        tagline: 'Smart Comparison',
      },
      {
        name: 'Localrent',
        desc: 'Rent directly from local owners — no hidden fees, no deposits. Ideal for budget-conscious travellers.',
        url: 'https://localrent.tpm.lv/qq1aKn1w',
        icon: Car,
        color: 'from-teal-500 to-teal-600',
        tagline: 'No Hidden Fees',
      },
      {
        name: 'Economybookings',
        desc: 'Affordable car hire in 30,000+ locations worldwide. Low-cost options for every budget.',
        url: 'https://economybookings.tpm.lv/ZTRwuuJf',
        icon: Car,
        color: 'from-emerald-500 to-emerald-600',
        tagline: 'Budget Friendly',
      },
      {
        name: 'GetRentacar',
        desc: 'Local car rental deals with transparent pricing. No credit card required at many locations.',
        url: 'https://getrentacar.tpm.lv/k2fzyJ8h',
        icon: Car,
        color: 'from-cyan-500 to-cyan-600',
        tagline: 'No Credit Card Needed',
      },
    ],
  },
  {
    category: 'Transfers & Transportation',
    items: [
      {
        name: 'Welcome Pickups',
        desc: 'Pre-book airport pickups with professional, English-speaking drivers. Tracked flights, no extra charges for delays.',
        url: 'https://tpm.lv/sD3CoTaI',
        icon: Users,
        color: 'from-green-500 to-green-600',
        tagline: 'Stress-Free Arrival',
      },
      {
        name: 'GetTransfer',
        desc: 'Private door-to-door transfers between cities, airports, and hotels. Compare driver offers and book instantly.',
        url: 'https://gettransfer.tpm.lv/CDo528tq',
        icon: Car,
        color: 'from-ocean to-ocean-light',
        tagline: 'Compare & Book',
      },
      {
        name: 'Kiwitaxi',
        desc: 'Book airport transfers and intercity rides worldwide. Fixed prices, meet & greet, and door-to-door service.',
        url: 'https://kiwitaxi.tpm.lv/oSXEVK28',
        icon: MapPin,
        color: 'from-lime-500 to-lime-600',
        tagline: 'Fixed Price Transfers',
      },
      {
        name: 'Intui Travel',
        desc: 'Airport shuttle and private transfer bookings in 150+ countries. Reliable, affordable, and easy to book.',
        url: 'https://intui.tpm.lv/lfzcVO2u',
        icon: MapPin,
        color: 'from-indigo-500 to-indigo-600',
        tagline: '150+ Countries',
      },
    ],
  },
  {
    category: 'Activities & Attractions',
    items: [
      {
        name: 'Tiqets',
        desc: 'Skip-the-line tickets to museums, attractions, and experiences worldwide. Instant confirmation, flexible booking.',
        url: 'https://tiqets.tpm.lv/NnUJBB3c',
        icon: Ticket,
        color: 'from-orange-500 to-orange-600',
        tagline: 'Skip the Line',
      },
    ],
  },
  {
    category: 'eSIM & Connectivity',
    items: [
      {
        name: 'Yesim',
        desc: 'Travel eSIM with data plans in 150+ countries. Stay connected without expensive roaming charges.',
        url: 'https://yesim.tpm.lv/8meY9Yc6',
        icon: Wifi,
        color: 'from-violet-500 to-violet-600',
        tagline: 'Global eSIM',
      },
      {
        name: 'Airalo',
        desc: 'Affordable eSIMs for 200+ countries. Buy, install, and connect in minutes — no physical SIM needed.',
        url: 'https://airalo.tpm.lv/8dyihntM',
        icon: Wifi,
        color: 'from-sky-500 to-sky-600',
        tagline: '200+ Countries',
      },
      {
        name: 'DrimSim',
        desc: 'Universal travel SIM card with low-cost data in 197 countries. Pay only for what you use.',
        url: 'https://drimsim.tpm.lv/S8aDP60k',
        icon: Wifi,
        color: 'from-pink-500 to-pink-600',
        tagline: 'Pay As You Go',
      },
    ],
  },
  {
    category: 'Travel Insurance',
    items: [
      {
        name: 'Ekta',
        desc: 'Comprehensive travel insurance covering medical, trip cancellation, and luggage. Instant policy delivery.',
        url: 'https://ektatraveling.tpm.lv/eZ9zv9vF',
        icon: ShieldCheck,
        color: 'from-amber-500 to-amber-600',
        tagline: 'Instant Coverage',
      },
    ],
  },
  {
    category: 'Flight Compensation',
    items: [
      {
        name: 'AirHelp',
        desc: 'Flight delayed, cancelled, or overbooked? Claim up to $600 compensation. No win, no fee — check eligibility in 2 minutes.',
        url: 'https://airhelp.tpm.lv/PWwYaq5O',
        icon: Plane,
        color: 'from-red-500 to-red-600',
        tagline: 'Up to $600 Compensation',
      },
      {
        name: 'Compensair',
        desc: 'Get money back for flight disruptions. Simple claim process, expert legal support, and you only pay if you win.',
        url: 'https://compensair.tpm.lv/ZOWHHas3',
        icon: Shield,
        color: 'from-purple-500 to-purple-600',
        tagline: 'No Win, No Fee',
      },
    ],
  },
  {
    category: 'Luggage Storage',
    items: [
      {
        name: 'Radical Storage',
        desc: 'Store your luggage safely near stations, airports, and attractions worldwide. Flat rate, fully insured.',
        url: 'https://radicalstorage.tpm.lv/SkQdLUhy',
        icon: Package,
        color: 'from-rose-500 to-rose-600',
        tagline: 'Flat Rate Storage',
      },
    ],
  },
];

export default function TravelServicesPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="travel-services-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="w-14 h-14 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ArrowRight className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Travel Services</h1>
            <p className="text-white/80">Everything you need for a perfect trip — all in one place</p>
          </motion.div>

          <div className="space-y-10">
            {SERVICES.map((section, sIdx) => (
              <motion.div key={section.category} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sIdx * 0.15 }}>
                <h2 className="font-serif text-xl text-white drop-shadow-lg mb-5 flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-gold rounded-full" />
                  {section.category}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {section.items.map((service) => (
                    <a key={service.name} href={service.url} target="_blank" rel="noopener noreferrer"
                      className="group bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-lg hover:shadow-2xl transition-all hover:border-gold/40 overflow-hidden"
                      data-testid={`service-${service.name.toLowerCase().replace(/\s/g, '-')}`}>
                      <div className="p-6">
                        <div className="flex items-start gap-4">
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                            <service.icon className="w-7 h-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-ocean font-bold text-lg">{service.name}</h3>
                              <span className="bg-white/90 text-ocean text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                {service.tagline}
                              </span>
                            </div>
                            <p className="text-ocean/60 text-sm leading-relaxed mb-4">{service.desc}</p>
                            <span className="inline-flex items-center gap-1.5 text-ocean font-semibold text-sm group-hover:text-gold transition-colors">
                              Visit {service.name} <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-12 text-center">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <AlertCircle className="w-8 h-8 text-gold mx-auto mb-3" />
              <p className="text-white font-serif text-lg mb-1">Need Help Choosing?</p>
              <p className="text-white/60 text-sm mb-4">Our team can help you find the best deals for your trip.</p>
              <a href="https://api.whatsapp.com/send?phone=61401050457&text=Hi%2C%20I%20need%20help%20with%20travel%20services" target="_blank" rel="noopener noreferrer">
                <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 h-10 font-semibold shadow-lg">
                  Chat with Us on WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
